<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
  import { Tooltip } from "@/components/ui/tooltip";
  import { getCountryName } from "@utils/constants/regions";
  import {
    calculateCountryTotal,
    calculateFeeTotal,
    calculateProjectTotals,
    FEE_FIELD_MAP,
    getCountryValue,
    getFeeValueOrDefault,
    formatCPI,
    formatCurrency,
    formatCurrencyCeil,
    formatInteger,
    getActiveTargets,
  } from "@/utils/pricer/pricerHelpers";
  import { usePricerProjectStore } from "@/stores/pricer/pricerProject";

  defineProps({
    serviceFees: { type: Array, required: true },
  });

  const { project } = storeToRefs(usePricerProjectStore());

  const hasAskLicence = computed(() => project.value.has_ask_licence === true);

  const totals = computed(() => calculateProjectTotals(project.value));

  const askLicenceDiscount = computed(() => project.value.ask_licence_discount || 0);
  const licenceTotalCost = computed(() => formatCurrency(totals.value.n * askLicenceDiscount.value));

  const showCol = (key) => !FEE_FIELD_MAP[key]?.hideOnNull || totals.value[key] > 0;

  const cols = computed(() => {
    const c = [
      { label: "Item", width: "max-w-[350px]" },
      { label: "Country", width: "max-w-[250px]" },
      { label: "N" },
      { label: "CPI", key: "cpi" },
    ];
    if (showCol("monitoring")) c.push({ label: "Monitoring" });
    if (showCol("translation")) c.push({ label: "Translation" });
    c.push({ label: "Total" });
    return c;
  });
</script>

<template>
  <div>
    <Table
      class="[&_th]:h-auto [&_th]:bg-muted [&_th]:px-3 [&_th]:py-2.5 [&_th]:text-3xs [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-[0.1em] [&_th]:text-foreground-subtle [&_thead_tr]:border-y [&_thead_tr]:border-secondary [&_td]:px-3 [&_td]:py-2 [&_td]:text-13 [&_tbody_tr]:border-secondary [&_tbody_tr:hover>td]:bg-muted [&_tbody_tr:hover>td:first-child]:rounded-l-lg [&_tbody_tr:hover>td:last-child]:rounded-r-lg">
    <TableHeader>
      <TableRow class="hover:bg-transparent">
        <TableHead
          v-for="col in cols"
          :key="col.label"
          :class="col.width">
          {{ col.label }}{{ col.key === 'cpi' && !hasAskLicence && askLicenceDiscount > 0 ? "*" : "" }}
        </TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      <template
        v-for="target in getActiveTargets(project)"
        :key="target.id">
        <TableRow
          v-for="row in target.countries"
          :key="row.id"
          class="hover:bg-transparent">
          <TableCell class="font-medium max-w-[350px]">
            <Tooltip :content="target.name">
              <div class="truncate">{{ target.name }}</div>
            </Tooltip>
          </TableCell>
          <TableCell class="max-w-[250px] truncate">{{ row.country ? getCountryName(row.country) : "-" }}</TableCell>
          <TableCell>{{ formatInteger(row.n) }}</TableCell>
          <TableCell>{{ formatCPI(getCountryValue(row, "cpi_manual", "cpi_auto")) }}</TableCell>
          <TableCell v-if="showCol('monitoring')">{{
            formatCurrencyCeil(getFeeValueOrDefault(row.monitoring_manual, row.monitoring_auto))
          }}</TableCell>
          <TableCell v-if="showCol('translation')">{{ formatCurrencyCeil(row.translation_manual || 0) }}</TableCell>
          <TableCell>{{ formatCurrencyCeil(calculateCountryTotal(row)) }}</TableCell>
        </TableRow>
      </template>

      <TableRow
        v-for="fee in serviceFees.filter((f) => f.show && !f.isMultiTargetsCountry)"
        :key="fee.type"
        class="hover:bg-transparent">
        <TableCell class="font-medium max-w-[350px]">
          <Tooltip :content="fee.label">
            <div class="truncate">{{ fee.label }}</div>
          </Tooltip>
        </TableCell>
        <TableCell class="text-muted-foreground">-</TableCell>
        <TableCell class="text-muted-foreground">-</TableCell>
        <TableCell class="text-muted-foreground">-</TableCell>
        <TableCell
          v-if="showCol('monitoring')"
          class="text-muted-foreground"
          >-</TableCell
        >
        <TableCell
          v-if="showCol('translation')"
          class="text-muted-foreground"
          >-</TableCell
        >
        <TableCell class="font-medium">{{ formatCurrencyCeil(calculateFeeTotal(project, fee.type)) }}</TableCell>
      </TableRow>
    </TableBody>
    <TableFooter class="border-t border-border bg-transparent">
      <TableRow class="hover:bg-transparent">
        <TableCell class="font-semibold text-primary">Total</TableCell>
        <TableCell />
        <TableCell class="font-semibold">{{ formatInteger(totals.n) }}</TableCell>
        <TableCell class="font-semibold">{{ formatCPI(totals.cpiAvg) }}</TableCell>
        <TableCell
          v-if="showCol('monitoring')"
          class="font-semibold"
          >{{ formatCurrencyCeil(totals.monitoring) }}</TableCell
        >
        <TableCell
          v-if="showCol('translation')"
          class="font-semibold"
          >{{ formatCurrencyCeil(totals.translation) }}</TableCell
        >
        <TableCell class="font-semibold">{{ formatCurrencyCeil(totals.total) }}</TableCell>
      </TableRow>
    </TableFooter>
  </Table>
    <p
      v-if="!hasAskLicence && askLicenceDiscount > 0"
      class="text-xs text-muted-foreground mt-2">
      *The CPI includes a {{ formatCurrency(askLicenceDiscount) }} platform usage fee per respondent, for a total of {{ licenceTotalCost }} (Ask – Pay As You Go).
    </p>
  </div>
</template>
